import { BUSINESS_DECISION_MARKER, RETENTION_ENTITY_LABELS } from '@kortex/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Field, Loading, Modal, Select, TextInput, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/**
 * Retention policies, from the platform console.
 *
 * How long each kind of record is kept, and what happens after. A platform
 * default applies to every account; an override replaces it for one, and a
 * disabled override holds that account back. Audit history and business
 * records stay disabled until somebody records whose decision it was to turn
 * them on - the server refuses otherwise.
 */

interface Policy {
  id: string;
  entity: string;
  tenantId: string | null;
  tenantName: string | null;
  retentionDays: number;
  action: 'DELETE' | 'ANONYMIZE' | 'PURGE_SENSITIVE_FIELDS';
  enabled: boolean;
  note: string | null;
  legalBasis: string | null;
  global: boolean;
  legallySignificant: boolean;
  decisionRequired: boolean;
}

interface PreviewPass {
  entity: string;
  scope: string;
  affected: number;
}

const ACTION_LABELS: Record<Policy['action'], string> = {
  DELETE: 'Delete',
  ANONYMIZE: 'Erase personal data',
  PURGE_SENSITIVE_FIELDS: 'Strip sensitive fields',
};

const label = (entity: string) => RETENTION_ENTITY_LABELS[entity] ?? entity;

export function RetentionTab() {
  const { session } = useAuth();
  const isSuperadmin = session?.platformRole === 'SUPERADMIN';
  const [editing, setEditing] = useState<Policy | 'override' | null>(null);
  const [preview, setPreview] = useState<Map<string, number> | null>(null);
  const { fail } = useToast();

  const policies = useQuery({
    queryKey: ['platform-retention'],
    queryFn: () => api.get<Policy[]>('/api/platform/privacy/retention'),
  });

  const runPreview = useMutation({
    mutationFn: () => api.get<PreviewPass[]>('/api/platform/privacy/retention/preview'),
    onSuccess: (passes) => setPreview(new Map(passes.map((pass) => [`${pass.entity}:${pass.scope}`, pass.affected]))),
    onError: fail,
  });

  if (policies.isLoading) return <Loading />;
  if (policies.error) return <ErrorState error={policies.error} onRetry={() => void policies.refetch()} />;
  const rows = policies.data ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h3>Retention</h3>
        {isSuperadmin ? (
          <div className="row gap-6">
            <button className="btn sm" disabled={runPreview.isPending} onClick={() => runPreview.mutate()}>
              {runPreview.isPending ? 'Counting…' : 'Preview what is due'}
            </button>
            <button className="btn sm" onClick={() => setEditing('override')}>
              Add account override
            </button>
          </div>
        ) : null}
      </div>
      <div className="card-body small muted">
        Applied nightly by the <span className="mono">apply-retention</span> job. Short-lived codes and
        telemetry run on technical defaults. Audit history and business records are kept until a period is
        decided and recorded here - the periods shown for them are placeholders, not decisions. Erased and
        expired records leave the live database; encrypted backups expire on their own schedule.
      </div>
      {rows.length === 0 ? (
        <Empty message="No retention policies" />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Records</th>
                <th>Applies to</th>
                <th className="num">Days</th>
                <th>Then</th>
                <th>State</th>
                {preview ? <th className="num">Due now</th> : null}
                <th>Why (legal basis)</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <b>{label(row.entity)}</b>
                  </td>
                  <td>{row.tenantId ? row.tenantName ?? row.tenantId.slice(0, 8) : 'Every account'}</td>
                  <td className="num">{row.retentionDays}</td>
                  <td>{ACTION_LABELS[row.action]}</td>
                  <td>
                    {row.enabled ? <span className="chip ok">On</span> : <span className="chip">Off</span>}
                    {row.decisionRequired ? (
                      <span className="chip warn" style={{ marginLeft: 6 }}>
                        Decision required
                      </span>
                    ) : null}
                  </td>
                  {preview ? (
                    <td className="num">{preview.get(`${row.entity}:${row.tenantId ?? 'platform'}`) ?? '—'}</td>
                  ) : null}
                  <td className="xs muted">
                    {row.legalBasis ?? '—'}
                    {row.note ? <div>{row.note}</div> : null}
                  </td>
                  <td>
                    {isSuperadmin ? (
                      <button className="btn sm" onClick={() => setEditing(row)}>
                        Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing ? (
        <PolicyEditor
          policy={editing === 'override' ? null : editing}
          policies={rows}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function PolicyEditor({ policy, policies, onClose }: { policy: Policy | null; policies: Policy[]; onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const overridable = policies.filter((row) => !row.tenantId && !row.global);
  const [entity, setEntity] = useState(policy?.entity ?? overridable[0]?.entity ?? '');
  const [tenantId, setTenantId] = useState(policy?.tenantId ?? '');
  const base = policy ?? overridable.find((row) => row.entity === entity) ?? null;
  const [days, setDays] = useState(String(policy?.retentionDays ?? base?.retentionDays ?? 30));
  const [enabled, setEnabled] = useState(policy?.enabled ?? false);
  const [note, setNote] = useState(policy?.note ?? '');
  const [legalBasis, setLegalBasis] = useState(policy?.legalBasis ?? base?.legalBasis ?? '');
  const [q, setQ] = useState('');

  const tenants = useQuery({
    queryKey: ['platform', 'tenants', 'retention', q],
    enabled: !policy,
    queryFn: () =>
      api.get<{ entries: Array<{ tenantId: string; name: string; city: string | null }> }>(
        `/api/platform/tenants?${new URLSearchParams({ limit: '50', ...(q ? { q } : {}) }).toString()}`,
      ),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put('/api/platform/privacy/retention', {
        entity,
        tenantId: (policy ? policy.tenantId : tenantId) || null,
        retentionDays: Number(days),
        action: base?.action,
        enabled,
        note,
        legalBasis,
      }),
    onSuccess: () => {
      push('Retention saved', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-retention'] });
      onClose();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/platform/privacy/retention/${policy?.id}`),
    onSuccess: () => {
      push('Override removed - the default applies again', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-retention'] });
      onClose();
    },
    onError: fail,
  });

  const significant = base?.legallySignificant ?? false;
  const stillUndecided = note.includes(BUSINESS_DECISION_MARKER) || note.trim() === '';

  return (
    <Modal
      title={policy ? `Retention - ${label(policy.entity)}` : 'Override retention for one account'}
      onClose={onClose}
      footer={
        <>
          {policy?.tenantId ? (
            <button className="btn danger" disabled={remove.isPending} onClick={() => remove.mutate()} style={{ marginRight: 'auto' }}>
              Remove override
            </button>
          ) : null}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={save.isPending || !entity || (!policy && !tenantId) || !(Number(days) >= 1) || (significant && enabled && stillUndecided)}
            onClick={() => save.mutate()}
          >
            Save
          </button>
        </>
      }
    >
      <div className="col gap-14">
        {!policy ? (
          <>
            <Select
              label="Records"
              value={entity}
              onChange={(e) => {
                setEntity(e.target.value);
                const row = overridable.find((candidate) => candidate.entity === e.target.value);
                if (row) setDays(String(row.retentionDays));
              }}
            >
              {overridable.map((row) => (
                <option key={row.entity} value={row.entity}>
                  {label(row.entity)}
                </option>
              ))}
            </Select>
            <TextInput label="Find the account" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select label="Account" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              <option value="">Choose…</option>
              {(tenants.data?.entries ?? []).map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>
                  {tenant.name}
                  {tenant.city ? ` - ${tenant.city}` : ''}
                </option>
              ))}
            </Select>
          </>
        ) : null}
        <TextInput
          label="Keep for (days)"
          type="number"
          min={1}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          hint={base ? `Then: ${ACTION_LABELS[base.action]}` : undefined}
        />
        <label className="checkbox">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {policy?.tenantId || !policy
            ? 'On (off holds this account back from the default - a legal hold)'
            : 'On'}
        </label>
        <Field
          label="Note"
          hint={
            significant
              ? 'Turning this on removes business records or audit history. Record whose decision it is - an approval reference - in place of the placeholder.'
              : 'Why this period.'
          }
        >
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Field
          label="Legal basis"
          hint="The law or business need this period rests on. Leave &quot;To confirm&quot; until your adviser has confirmed it."
        >
          <textarea className="input" rows={2} value={legalBasis} onChange={(e) => setLegalBasis(e.target.value)} />
        </Field>
        {significant && enabled && stillUndecided ? (
          <div className="form-error">Record the decision in the note before turning this on.</div>
        ) : null}
      </div>
    </Modal>
  );
}
